import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Paperclip, Download, Trash2, Upload, FileText, Image as ImageIcon, Pencil, Check, X } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const isImage = (ct = "") => ct.startsWith("image/");
const humanSize = (b = 0) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};
const humanDate = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR") : "-");

/**
 * Row-based attachments panel. Left column: name/meta/actions; right column:
 * thumbnail (images) or file icon (PDFs / other).
 * - readOnly hides upload / rename / delete controls
 * - accept restricts file picker MIME types
 */
export default function AttachmentsSection({
  ownerKind,
  ownerId,
  title = "Dosyalar",
  readOnly = false,
  accept = "image/*,application/pdf",
  testIdPrefix = "attachments",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewIds, setPreviewIds] = useState({}); // id -> blob URL
  const [confirmDel, setConfirmDel] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!ownerKind || !ownerId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/attachments", { params: { owner_kind: ownerKind, owner_id: ownerId } });
      setItems(data);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  }, [ownerKind, ownerId]);

  useEffect(() => { load(); }, [load]);

  // Build blob previews for images so <img> works with cookie auth.
  useEffect(() => {
    let alive = true;
    const created = [];
    const build = async () => {
      const next = {};
      for (const it of items) {
        if (!isImage(it.content_type)) continue;
        try {
          const r = await api.get(`/attachments/${it.id}/download`, { responseType: "blob" });
          const url = URL.createObjectURL(r.data);
          if (!alive) { URL.revokeObjectURL(url); return; }
          next[it.id] = url;
          created.push(url);
        } catch { /* ignore */ }
      }
      if (alive) setPreviewIds(next);
    };
    build();
    return () => { alive = false; created.forEach(u => URL.revokeObjectURL(u)); };
  }, [items]);

  const onPick = () => fileRef.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      const form = new FormData();
      form.append("owner_kind", ownerKind);
      form.append("owner_id", ownerId);
      form.append("file", f);
      try {
        await api.post("/attachments", form, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (err) {
        toast.error(err.response?.data?.detail || `${f.name} yüklenemedi`);
      }
    }
    setUploading(false);
    if (ok > 0) toast.success(`${ok} dosya yüklendi`);
    if (fileRef.current) fileRef.current.value = "";
    load();
  };

  const download = async (att) => {
    try {
      const r = await api.get(`/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename || "dosya";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error("İndirilemedi"); }
  };

  const openPreview = (att) => {
    const url = previewIds[att.id];
    if (url) window.open(url, "_blank");
    else download(att);
  };

  const startRename = (att) => {
    setRenamingId(att.id);
    // Pre-fill without extension so the user edits the "name" portion.
    const name = att.original_filename || "";
    const dot = name.lastIndexOf(".");
    setRenameValue(dot > 0 ? name.slice(0, dot) : name);
  };
  const cancelRename = () => { setRenamingId(null); setRenameValue(""); };
  const saveRename = async (att) => {
    const v = renameValue.trim();
    if (!v) { toast.error("Ad boş olamaz"); return; }
    try {
      await api.patch(`/attachments/${att.id}`, { original_filename: v });
      toast.success("Yeniden adlandırıldı");
      setRenamingId(null);
      setRenameValue("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const doDelete = async () => {
    const att = confirmDel;
    setConfirmDel(null);
    if (!att) return;
    try {
      await api.delete(`/attachments/${att.id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-section`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-zinc-700 flex items-center gap-2">
          <Paperclip size={14} />{title} ({items.length})
        </div>
        {!readOnly && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept={accept}
              multiple
              onChange={onFiles}
              className="hidden"
              data-testid={`${testIdPrefix}-file-input`}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onPick}
              disabled={uploading}
              data-testid={`${testIdPrefix}-upload-button`}
              className="h-8"
            >
              <Upload size={14} className="mr-1" />{uploading ? "Yükleniyor..." : "Dosya Yükle"}
            </Button>
          </>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-zinc-500">Yükleniyor...</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-zinc-500 border border-dashed border-zinc-200 rounded p-4 text-center">
          Henüz dosya eklenmedi.
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100 border border-zinc-200 rounded-md bg-white">
          {items.map((att) => {
            const img = isImage(att.content_type);
            const thumb = previewIds[att.id];
            const isRenaming = renamingId === att.id;
            return (
              <li
                key={att.id}
                data-testid={`${testIdPrefix}-item-${att.id}`}
                className="flex items-center gap-4 px-3 py-2.5"
              >
                {/* Left: name + meta + actions */}
                <div className="flex-1 min-w-0">
                  {isRenaming ? (
                    <div className="flex items-center gap-2">
                      <Input
                        autoFocus
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveRename(att); if (e.key === "Escape") cancelRename(); }}
                        className="h-8 text-sm"
                        data-testid={`${testIdPrefix}-rename-input-${att.id}`}
                      />
                      <button
                        type="button"
                        onClick={() => saveRename(att)}
                        className="p-1 rounded hover:bg-emerald-50 text-emerald-700"
                        data-testid={`${testIdPrefix}-rename-save-${att.id}`}
                        title="Kaydet"
                      ><Check size={16} /></button>
                      <button
                        type="button"
                        onClick={cancelRename}
                        className="p-1 rounded hover:bg-zinc-100 text-zinc-500"
                        title="İptal"
                      ><X size={16} /></button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openPreview(att)}
                      className="block truncate text-sm font-medium text-zinc-800 hover:underline text-left"
                      title={att.original_filename}
                    >{att.original_filename}</button>
                  )}
                  <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>{humanSize(att.size)}</span>
                    <span>·</span>
                    <span>{humanDate(att.created_at)}</span>
                    {att.uploaded_by_name && (
                      <>
                        <span>·</span>
                        <span>{att.uploaded_by_name}</span>
                      </>
                    )}
                  </div>
                  {!isRenaming && (
                    <div className="flex items-center gap-3 mt-1.5">
                      <button
                        type="button"
                        onClick={() => download(att)}
                        className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1"
                        data-testid={`${testIdPrefix}-download-${att.id}`}
                      ><Download size={12} />İndir</button>
                      {!readOnly && (
                        <>
                          <button
                            type="button"
                            onClick={() => startRename(att)}
                            className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1"
                            data-testid={`${testIdPrefix}-rename-${att.id}`}
                          ><Pencil size={12} />Yeniden Adlandır</button>
                          <button
                            type="button"
                            onClick={() => setConfirmDel(att)}
                            className="text-xs text-zinc-500 hover:text-red-600 inline-flex items-center gap-1"
                            data-testid={`${testIdPrefix}-delete-${att.id}`}
                          ><Trash2 size={12} />Sil</button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Right: thumbnail / file icon */}
                <button
                  type="button"
                  onClick={() => openPreview(att)}
                  className="shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-md bg-zinc-50 border border-zinc-200 flex items-center justify-center overflow-hidden"
                  title={att.original_filename}
                  data-testid={`${testIdPrefix}-thumb-${att.id}`}
                >
                  {img && thumb ? (
                    <img src={thumb} alt={att.original_filename} className="w-full h-full object-cover" />
                  ) : img ? (
                    <ImageIcon size={22} className="text-zinc-300" />
                  ) : (
                    <FileText size={22} className="text-zinc-400" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <AlertDialog open={!!confirmDel} onOpenChange={(v) => { if (!v) setConfirmDel(null); }}>
        <AlertDialogContent data-testid={`${testIdPrefix}-delete-confirm`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Dosyayı sil</AlertDialogTitle>
            <AlertDialogDescription>&quot;{confirmDel?.original_filename}&quot; silinsin mi?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid={`${testIdPrefix}-delete-confirm-btn`}>Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
